import React, { useState, useEffect, useCallback } from 'react';
import { Wrench, Save, Loader2, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReadingMethodsEditor({ token }) {
  const [methods, setMethods] = useState([]);
  const [csv, setCsv] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/reading-methods`);
      if (!r.ok) return;
      const data = await r.json();
      setMethods(Array.isArray(data) ? data : []);
      setCsv((Array.isArray(data) ? data : []).join(', '));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCsv = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/reading-methods`, {
        method: 'PUT', headers, body: JSON.stringify({ methods_csv: csv }),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally { setSaving(false); }
  };

  const removeOne = async (m) => {
    const next = methods.filter((x) => x !== m);
    setMethods(next);
    setCsv(next.join(', '));
    await fetch(`${API_URL}/api/config/reading-methods`, {
      method: 'PUT', headers, body: JSON.stringify({ methods: next }),
    });
  };

  const addOne = async () => {
    const v = window.prompt('Enter a new reading-method name:');
    if (!v) return;
    const next = [...new Set([...methods, v.trim()])].filter(Boolean);
    setMethods(next);
    setCsv(next.join(', '));
    await fetch(`${API_URL}/api/config/reading-methods`, {
      method: 'PUT', headers, body: JSON.stringify({ methods: next }),
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading reading methods…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="reading-methods-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Wrench className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">Reading Methods (ECU tools)</p>
          <p className="text-cyan-800">
            These appear in the New Order wizard, in the <em>Reading method</em> dropdown.
            Edit the comma-separated list below or add/remove items individually.
          </p>
        </div>
      </div>

      {/* CSV editor */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h3 className="font-bold text-slate-800 mb-1">Comma-separated list</h3>
        <p className="text-xs text-slate-500 mb-3">
          Tip: separate items with commas. Duplicates are merged automatically.
        </p>
        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          rows={6}
          data-testid="reading-methods-csv"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-cyan-500 outline-none"
        />
        <div className="flex gap-2 mt-3 items-center">
          <button
            onClick={saveCsv}
            disabled={saving}
            data-testid="save-reading-methods"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save list
          </button>
          {savedFlash && (
            <span className="text-emerald-600 text-sm font-medium" data-testid="reading-methods-saved">✓ Saved</span>
          )}
        </div>
      </div>

      {/* Chip list */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-800">Active methods <span className="text-slate-400 font-normal">({methods.length})</span></h3>
            <p className="text-xs text-slate-500">Click × to remove an item.</p>
          </div>
          <button
            onClick={addOne}
            data-testid="add-reading-method"
            className="px-4 py-2 bg-cyan-50 text-cyan-700 rounded-lg text-sm font-bold hover:bg-cyan-100 flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add method
          </button>
        </div>
        {methods.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No methods configured — clients won't see a dropdown.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {methods.map((m) => (
              <span key={m} className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-xs font-semibold border border-slate-200" data-testid={`reading-method-chip-${m}`}>
                {m}
                <button onClick={() => removeOne(m)} className="ml-1 text-slate-400 hover:text-rose-600" title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
